<template>
  <div
    ref="root"
    class="flex flex-col gap-3 rounded-lg border border-blue-200 bg-white p-4"
    role="group"
    :aria-label="`${editing ? 'Edit shipment' : 'Plan a shipment'} for ${poNumber}`"
    @keydown="onKeydown"
  >
    <b class="text-sm text-gray-900">{{ editing ? 'Edit shipment' : 'Plan a shipment' }}</b>

    <div class="flex flex-wrap items-start gap-3">
      <div class="flex flex-col gap-1">
        <label class="label flex flex-col gap-1">
          Ship date
          <input
            v-model="plannedDate"
            type="date"
            class="input h-9"
            :class="bad('plannedDate')"
            data-field="plannedDate"
            :aria-invalid="error?.field === 'plannedDate'"
            :aria-describedby="describedBy('plannedDate')"
          />
        </label>
        <span v-if="error?.field === 'plannedDate'" :id="errorId('plannedDate')" class="text-xs text-red-700">{{ error.message }}</span>
      </div>
      <template v-if="moved">
        <div class="flex flex-col gap-1">
          <label class="label flex flex-col gap-1">
            Why the new date?
            <select
              v-model="reason"
              class="select h-9"
              :class="bad('reason')"
              data-field="reason"
              :aria-invalid="error?.field === 'reason'"
              :aria-describedby="describedBy('reason')"
            >
              <option value="">Choose a reason</option>
              <option v-for="r in MOVE_REASONS" :key="r" :value="r">{{ moveReasonLabel(r) }}</option>
            </select>
          </label>
          <span v-if="error?.field === 'reason'" :id="errorId('reason')" class="text-xs text-red-700">{{ error.message }}</span>
        </div>
        <div v-if="reason === MOVE_REASON.OTHER" class="flex flex-col gap-1">
          <label class="label flex flex-col gap-1">
            Other reason
            <input
              v-model="reasonText"
              class="input h-9"
              :class="bad('reasonText')"
              data-field="reasonText"
              maxlength="200"
              placeholder="e.g. waiting for customer drawing"
              :aria-invalid="error?.field === 'reasonText'"
              :aria-describedby="describedBy('reasonText')"
            />
          </label>
          <span v-if="error?.field === 'reasonText'" :id="errorId('reasonText')" class="text-xs text-red-700">{{ error.message }}</span>
        </div>
      </template>
      <div class="flex min-w-0 flex-1 basis-60 flex-col gap-1">
        <label class="label flex flex-col gap-1">
          Note
          <input
            v-model="note"
            class="input h-9"
            :class="bad('note')"
            data-field="note"
            placeholder="Optional"
            :aria-invalid="error?.field === 'note'"
            :aria-describedby="describedBy('note')"
          />
        </label>
        <span v-if="error?.field === 'note'" :id="errorId('note')" class="text-xs text-red-700">{{ error.message }}</span>
      </div>
    </div>

    <LineTable v-model="drafts" :lines="lines" :left="left" :error-field="lineErrorField" :error-id="errorId('lines')" />
    <p v-if="lineErrorField && error" :id="errorId('lines')" class="m-0 text-sm text-red-700" role="alert">{{ error.message }}</p>
    <p v-if="serverError" class="m-0 text-sm text-red-700" role="alert">{{ serverError }}</p>

    <div class="flex flex-wrap items-center justify-end gap-3">
      <button
        v-if="editing"
        type="button"
        class="mr-auto text-sm font-semibold text-red-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="busy"
        @click="confirmOpen = true"
      >
        {{ removing ? 'Removing…' : 'Remove shipment' }}
      </button>
      <button type="button" class="btn-secondary" @click="emit('close')">Cancel</button>
      <button type="button" class="btn-primary" :disabled="busy" @click="save">{{ saveLabel }}</button>
    </div>

    <ConfirmDialog
      :open="confirmOpen"
      title="Remove shipment"
      message="Remove this shipment? Its pieces are to plan again."
      confirm-label="Remove shipment"
      @cancel="confirmOpen = false"
      @confirm="remove"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import LineTable from '@/components/poTracker/LineTable.vue';
  import { useFieldError } from '@/composables/useFieldError';
  import logger from '@/services/logger';
  import { editShipment, planShipment, removeShipment } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { errorStatus, extractErrorMessage } from '@/utils/apiError';
  import { MOVE_REASON, type MoveReason, type PoLineQuantities, type Shipment } from '@/types/poTracker';
  import { editBody, fmtDate, initialDrafts, leftForForm, moveReasonLabel, planBody, validatePlan, validationLeft, type FieldError, type LineDraft } from '@/utils/poTracker';

  const MOVE_REASONS = Object.values(MOVE_REASON);
  const HTTP_NOT_FOUND = 404;

  const props = defineProps<{ poNumber: string; vendorId: string; lines: PoLineQuantities[]; editing: Shipment | null }>();
  const emit = defineEmits<{ close: [] }>();

  const store = usePoTrackerStore();
  const root = ref<HTMLElement | null>(null);

  const left = computed(() => leftForForm(props.lines, props.editing));
  const plannedDate = ref(props.editing?.plannedDate ?? '');
  const reason = ref<MoveReason | ''>('');
  const reasonText = ref('');
  const note = ref(props.editing?.note ?? '');
  const drafts = ref<LineDraft[]>(initialDrafts(props.lines, props.editing, left.value));
  const error = ref<FieldError | null>(null);
  const serverError = ref<string | null>(null);
  const saving = ref(false);
  const confirmOpen = ref(false);
  const removing = ref(false);
  // One request at a time: an edit and a remove must never race for the same shipment.
  const busy = computed(() => saving.value || removing.value);

  const saveLabel = computed(() => {
    if (saving.value) return 'Saving…';
    return props.editing ? 'Save' : 'Plan shipment';
  });

  const moved = computed(() => props.editing !== null && plannedDate.value !== props.editing.plannedDate);
  const lineErrorField = computed(() => (error.value && (error.value.field === 'lines' || error.value.field.startsWith('qty-')) ? error.value.field : null));

  const { errorId, describedBy, bad } = useFieldError(error);

  onMounted(() => focusField('plannedDate'));

  function focusField(field: string): void {
    void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus());
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      emit('close');
      return;
    }
    // Enter on a button, a checkbox or the line filter keeps its own meaning.
    const target = event.target as HTMLElement;
    if (
      event.key === 'Enter' &&
      !target.hasAttribute('data-no-submit') &&
      (target.tagName === 'INPUT' || target.tagName === 'SELECT') &&
      (target as HTMLInputElement).type !== 'checkbox'
    ) {
      event.preventDefault();
      void save();
    }
  }

  async function save(): Promise<void> {
    if (busy.value) return;
    serverError.value = null;
    error.value = validatePlan({
      plannedDate: plannedDate.value,
      lines: drafts.value,
      left: validationLeft(left.value, props.editing, drafts.value),
      moved: moved.value,
      reason: reason.value,
      reasonText: reasonText.value,
      note: note.value,
    });
    if (error.value) {
      focusField(error.value.field);
      return;
    }
    saving.value = true;
    try {
      if (props.editing) {
        const body = editBody(props.editing, { plannedDate: plannedDate.value, reason: reason.value, reasonText: reasonText.value, note: note.value, lines: drafts.value });
        if (Object.keys(body).length > 0) await editShipment(props.editing.id, body);
        logger.showSuccess('Saved');
      } else {
        await planShipment(props.poNumber, planBody(props.vendorId, { plannedDate: plannedDate.value, note: note.value, lines: drafts.value }));
        logger.showSuccess(`Shipment planned for ${fmtDate(plannedDate.value)}`);
      }
      emit('close');
      await store.afterWrite([props.poNumber]);
    } catch (saveError) {
      serverError.value = extractErrorMessage(saveError);
    } finally {
      saving.value = false;
    }
  }

  async function remove(): Promise<void> {
    confirmOpen.value = false;
    if (!props.editing || busy.value) return;
    removing.value = true;
    serverError.value = null;
    try {
      await removeShipment(props.editing.id);
      logger.showSuccess('Shipment removed');
    } catch (removeError) {
      // A 404 means someone else removed it first; anything else keeps the form open with the reason.
      if (errorStatus(removeError) !== HTTP_NOT_FOUND) {
        serverError.value = extractErrorMessage(removeError);
        return;
      }
      logger.showInfo('Already removed.');
    } finally {
      removing.value = false;
    }
    emit('close');
    await store.afterWrite([props.poNumber]);
  }
</script>
